// Copied from platform-app config/buttonRegistry.ts on 2026-10-14 for Super Admin → POS Defaults — keep in step with the original.
import type { CSSProperties } from 'react';

export interface ButtonAppearance {
  label: string;
  icon: string;
  iconColor: string;
  bgColor: string;
  textColor: string;
  bgType?: 'solid' | 'gradient';
  gradientFrom?: string;
  gradientTo?: string;
  gradientAngle?: number;
}

export interface ButtonRegistryEntry extends ButtonAppearance {
  group: 'sales' | 'register' | 'employee' | 'admin';
}

export interface ButtonOverride extends Partial<ButtonAppearance> {
  requiresApproval?: boolean;
  visibility?: 'all' | 'admin' | 'employee';
  active?: boolean;
  customItem?: {
    availableTaxIds?: string[];
    availableFeeIds?: string[];
    defaultTaxIds?: string[];
    defaultFeeIds?: string[];
    // When true, custom items added via this button earn loyalty points at
    // the tenant's global base earn rate (no category/product bonuses since
    // custom items have no productId/categoryId). When false or undefined,
    // custom items are excluded from loyalty points (default).
    loyaltyEarnsPoints?: boolean;
  };
  employeeDiscount?: {
    enabled?: boolean;
    percentage: number;
  };
}

export const BUTTON_REGISTRY: Record<string, ButtonRegistryEntry> = {
  'void':                 { label: 'Void',           icon: 'x',                 iconColor: '#ffffff', bgColor: '#ef4444', textColor: '#ffffff', group: 'sales' },
  'refund':               { label: 'Refund',         icon: 'rotate-ccw',        iconColor: '#ffffff', bgColor: '#f87171', textColor: '#ffffff', group: 'sales' },
  'exchange':             { label: 'Exchange',       icon: 'arrow-left-right',  iconColor: '#ffffff', bgColor: '#fb923c', textColor: '#ffffff', group: 'sales' },
  'discount':             { label: 'Discount',       icon: 'percent',           iconColor: '#ffffff', bgColor: '#60a5fa', textColor: '#ffffff', group: 'sales' },
  'hold':                 { label: 'Hold',           icon: 'pause',             iconColor: '#ffffff', bgColor: '#a78bfa', textColor: '#ffffff', group: 'sales' },
  'open-order':           { label: 'Open Order',     icon: 'play',              iconColor: '#ffffff', bgColor: '#c084fc', textColor: '#ffffff', group: 'sales' },
  'rewards':              { label: 'Rewards',        icon: 'gift',              iconColor: '#ffffff', bgColor: '#4ade80', textColor: '#ffffff', group: 'sales' },
  'search':               { label: 'Search',         icon: 'search',            iconColor: '#ffffff', bgColor: '#3b82f6', textColor: '#ffffff', group: 'sales' },
  'customer':             { label: 'Customer',       icon: 'user',              iconColor: '#ffffff', bgColor: '#3b82f6', textColor: '#ffffff', group: 'sales' },
  'transactions':         { label: 'Transactions',   icon: 'file-text',         iconColor: '#ffffff', bgColor: '#a5b4fc', textColor: '#ffffff', group: 'sales' },
  'cash-tip':             { label: 'Cash Tip',       icon: 'coins',             iconColor: '#ffffff', bgColor: '#4ade80', textColor: '#ffffff', group: 'sales' },
  'deals':                { label: 'Deals',          icon: 'tag',               iconColor: '#ffffff', bgColor: '#fbbf24', textColor: '#ffffff', group: 'sales' },
  'damage':               { label: 'Damage',         icon: 'alert-triangle',    iconColor: '#ffffff', bgColor: '#f97316', textColor: '#ffffff', group: 'sales' },
  'open-drawer':          { label: 'Open Drawer',    icon: 'lock-open',         iconColor: '#ffffff', bgColor: '#60a5fa', textColor: '#ffffff', group: 'register' },
  'safe-drop':            { label: 'Safe Drop',      icon: 'lock',              iconColor: '#ffffff', bgColor: '#ef4444', textColor: '#ffffff', group: 'register' },
  'payout':               { label: 'Payout',         icon: 'arrow-up-from-line', iconColor: '#ffffff', bgColor: '#f87171', textColor: '#ffffff', group: 'register' },
  'new-product-request': { label: 'New Product',    icon: 'package-plus',      iconColor: '#ffffff', bgColor: '#f59e0b', textColor: '#ffffff', group: 'register' },
  'calculator':           { label: 'Calculator',     icon: 'calculator',        iconColor: '#ffffff', bgColor: '#60a5fa', textColor: '#ffffff', group: 'register' },
  'reports':              { label: 'Reports',        icon: 'bar-chart-3',       iconColor: '#ffffff', bgColor: '#60a5fa', textColor: '#ffffff', group: 'register' },
  'clock':                { label: 'Clock In/Out',   icon: 'clock',             iconColor: '#ffffff', bgColor: '#a78bfa', textColor: '#ffffff', group: 'employee' },
  'shift-info':           { label: 'Shift Info',     icon: 'info',              iconColor: '#ffffff', bgColor: '#a78bfa', textColor: '#ffffff', group: 'employee' },
  'end-shift':            { label: 'End Shift',      icon: 'power',             iconColor: '#ffffff', bgColor: '#ef4444', textColor: '#ffffff', group: 'employee' },
  'store-supply':         { label: 'Store Supply',   icon: 'package',           iconColor: '#ffffff', bgColor: '#8b5cf6', textColor: '#ffffff', group: 'sales' },
  'weighted-lookup':      { label: 'Weighted',       icon: 'scale',             iconColor: '#ffffff', bgColor: '#f59e0b', textColor: '#ffffff', group: 'sales' },
  'non-scannable-lookup': { label: 'Non-Scannable',  icon: 'search',            iconColor: '#ffffff', bgColor: '#6366f1', textColor: '#ffffff', group: 'sales' },
  'pick-list':            { label: 'Pick List',      icon: 'clipboard-list',    iconColor: '#ffffff', bgColor: '#14b8a6', textColor: '#ffffff', group: 'register' },
  'transfer':             { label: 'Transfers',      icon: 'arrow-left-right',  iconColor: '#ffffff', bgColor: '#0ea5e9', textColor: '#ffffff', group: 'register' },
  'custom-item':          { label: 'Custom Item',    icon: 'dollar-sign',       iconColor: '#ffffff', bgColor: '#10b981', textColor: '#ffffff', group: 'sales' },
  // Module 29 — Oil Blending. Mirrors POS_BUTTON_REGISTRY in shared-types.
  // Surfaces in the Action Bar Layout when super-admin saves them into the
  // terminal's `buttonLayout.available` array (gated by pkg_pos_oil_blending).
  'build-blend':          { label: 'Build Blend',    icon: 'sparkles',          iconColor: '#ffffff', bgColor: '#10b981', textColor: '#ffffff', group: 'sales' },
  'pending-blends':       { label: 'Pending Blends', icon: 'inbox',             iconColor: '#ffffff', bgColor: '#10b981', textColor: '#ffffff', group: 'sales' },
  'customer-display':     { label: 'Customer Display', icon: 'monitor',         iconColor: '#ffffff', bgColor: '#3b82f6', textColor: '#ffffff', group: 'register' },
  'lottery':              { label: 'Lotto',           icon: 'ticket',            iconColor: '#ffffff', bgColor: '#7c3aed', textColor: '#ffffff', group: 'sales' },
  // Gas & Fuel — prepaid fuel by amount or gallons. Mirrors POS_BUTTON_REGISTRY in shared-types.
  'gas':                  { label: 'Gas',            icon: 'fuel',              iconColor: '#ffffff', bgColor: '#d97706', textColor: '#ffffff', group: 'sales' },
  // Services (pkg_services) — appointments, walk-ins, job tickets, rentals. Mirrors POS_BUTTON_REGISTRY in shared-types.
  'services':             { label: 'Services',       icon: 'calendar-clock',    iconColor: '#ffffff', bgColor: '#0d9488', textColor: '#ffffff', group: 'sales' },
};

/**
 * POS Header — the fixed icon buttons beside the sale-screen search box.
 * Ids/order mirror POS_HEADER_BUTTON_IDS in @platform/shared-types
 * (pos-header-buttons.schema). Colours are the theme defaults the till paints
 * today (the search-field surface); a merchant override replaces them.
 * 'hold' / 'open-order' share their ids with the rail buttons so role access
 * and manager approval gate them the same way, but their header look is stored
 * separately on `buttonLayout.headerButtons`, not in `buttonCustomizations`.
 */
export const HEADER_BUTTON_IDS = ['scan', 'hold', 'open-order'] as const;
export type HeaderButtonId = typeof HEADER_BUTTON_IDS[number];

export const HEADER_BUTTON_REGISTRY: Record<HeaderButtonId, ButtonRegistryEntry> = {
  'scan':       { label: 'Scan',        icon: 'ScanBarcode', iconColor: '#18181b', bgColor: '#ffffff', textColor: '#18181b', group: 'sales' },
  'hold':       { label: 'Hold',        icon: 'PauseCircle', iconColor: '#18181b', bgColor: '#ffffff', textColor: '#18181b', group: 'sales' },
  'open-order': { label: 'Open orders', icon: 'ReceiptText', iconColor: '#18181b', bgColor: '#ffffff', textColor: '#18181b', group: 'sales' },
};

/** One header entry as stored: the id plus the same override shape as a rail button. */
export type HeaderButtonConfig = { id: HeaderButtonId } & Pick<
  ButtonOverride,
  'label' | 'icon' | 'bgColor' | 'textColor' | 'iconColor' | 'bgType' | 'gradientFrom' | 'gradientTo' | 'gradientAngle' | 'requiresApproval' | 'visibility' | 'active'
>;

/**
 * Exactly one entry per header id: saved entries first in saved order (unknown
 * ids / duplicates dropped), missing ids appended in default order. Absent or
 * [] = today's header. Mirrors normalizeHeaderButtons in shared-types.
 */
export function normalizeHeaderButtons(input: unknown): HeaderButtonConfig[] {
  const out: HeaderButtonConfig[] = [];
  const seen = new Set<string>();
  if (Array.isArray(input)) {
    for (const raw of input) {
      const id = raw && typeof raw === 'object' ? (raw as { id?: unknown }).id : undefined;
      if (typeof id !== 'string' || !(HEADER_BUTTON_IDS as readonly string[]).includes(id) || seen.has(id)) continue;
      seen.add(id);
      out.push({ ...(raw as HeaderButtonConfig) });
    }
  }
  for (const id of HEADER_BUTTON_IDS) if (!seen.has(id)) out.push({ id });
  return out;
}

/**
 * Merge registry defaults with per-terminal overrides.
 * Handles backward compatibility: old `color` field maps to `bgColor`.
 */
export function mergeButton(actionId: string, overrides?: ButtonOverride & { color?: string }, registryEntry?: ButtonAppearance): ButtonAppearance | null {
  // `registryEntry` lets a button that is not in BUTTON_REGISTRY (the POS Header
  // buttons) use the same merge with its own defaults.
  const defaults = registryEntry ?? BUTTON_REGISTRY[actionId];
  if (!defaults) return null;
  if (!overrides) return { label: defaults.label, icon: defaults.icon, iconColor: defaults.iconColor, bgColor: defaults.bgColor, textColor: defaults.textColor, bgType: defaults.bgType, gradientFrom: defaults.gradientFrom, gradientTo: defaults.gradientTo, gradientAngle: defaults.gradientAngle };

  const normalizedOverrides = { ...overrides };
  if (overrides.color && !overrides.bgColor) {
    normalizedOverrides.bgColor = overrides.color;
  }
  delete (normalizedOverrides as any).color;
  delete (normalizedOverrides as any).emoji;

  return { ...defaults, ...normalizedOverrides };
}

/**
 * Get background style for a merged button (handles solid and gradient).
 */
export function getButtonBgStyle(btn: ButtonAppearance): CSSProperties {
  if (btn.bgType === 'gradient' && btn.gradientFrom && btn.gradientTo) {
    return { background: `linear-gradient(${btn.gradientAngle || 135}deg, ${btn.gradientFrom}, ${btn.gradientTo})` };
  }
  return { background: btn.bgColor };
}
